import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState, type JSX } from 'react';
import { api } from '../../apigateway';
import { useVorgangContext } from '../../contexts/vorgangContext';
import { makeGraphqlQuery } from '../../graphql/makeGraphqlQuery';
import * as queries from '../../graphql/queries';
import { Gutachtenart, RechnungspositionEinheit } from '../../shared/constants';
import { getMessageFromError } from '../../shared/throw';
import { rechnungsparameterOperations } from '../../shared/url';
import { Mandant, VorgangRechnungspositionEintrag } from '../../types';
import { ERROR_MESSAGE } from '../common/Alert';
import { Euro } from '../common/Euro';
import { Modal } from '../Modal';
import { RechnungspositionenTabelle } from '../RechnungspositionenTabelle';
import './RechnungspositonenEditierenDialog.css';
import { SPACING_BETWEEN_BOXES } from '../common/spacings';
import { ermittleRechnungspositionEinheit, ermittleRechnungspositionLabel } from '../../shared/rechnungHelper';

type Props = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly aktualisiereRechnungspositionen: (rechnungspositionen: VorgangRechnungspositionEintrag[]) => void;
  readonly saveButtonText?: string;
};

type VorgangRechnungspositionEintragExtended = VorgangRechnungspositionEintrag & {
  selected?: boolean;
};

export function RechnungspositonenPruefenDialog({ open, onClose, aktualisiereRechnungspositionen, saveButtonText = 'Speichern' }: Props): JSX.Element {
  const { vorgang, isLoading } = useVorgangContext();

  const { enqueueSnackbar } = useSnackbar();
  const [calcRechnungspositionen, setCalcRechnungspositionen] = useState<VorgangRechnungspositionEintragExtended[]>([]);
  const [vorgangRechnungspositionen, setVorgangRechnungspositionen] = useState<VorgangRechnungspositionEintragExtended[]>([]);
  const [mandant, setMandant] = useState<Mandant | null>(null);
  const [mergedRechnungspositionen, setMergedRechnungspositionen] = useState<VorgangRechnungspositionEintragExtended[]>(vorgang.rechnungspositionen ?? []);

  useEffect(() => {
    const holeMandantDaten = async () => {
      const mandant = await makeGraphqlQuery<Mandant>(queries.holeMandant, {
        mandant: vorgang?.mandant
      });
      setMandant(mandant);
    };

    if (vorgang?.id) {
      holeMandantDaten();
      setVorgangRechnungspositionen((vorgang.editierteRechnungspositionen ?? []).map((rp) => ({ ...rp, selected: rp.geloescht ? false : true })));
    }
  }, [vorgang]);

  useEffect(() => {
    if (vorgang.id !== '') {
      api
        .request(rechnungsparameterOperations.get(vorgang.id))
        .then((rechnungspositionen) => {
          setCalcRechnungspositionen(
            rechnungspositionen.map((rp) => ({ ...rp, selected: !vorgang.editierteRechnungspositionen?.some((drp) => drp.titel === rp.titel) }))
          );
        })
        .catch((error) => enqueueSnackbar(`Fehler beim Laden der Rechnungspositionen: ${getMessageFromError(error)}`, ERROR_MESSAGE));
    }
  }, [enqueueSnackbar, vorgang.id, vorgang.editierteRechnungspositionen]);

  const selectPosition = (
    rechnungspositionen: VorgangRechnungspositionEintragExtended[],
    index: number,
    setFn: (rechnungspositionen: VorgangRechnungspositionEintragExtended[]) => void
  ) => {
    setFn(
      rechnungspositionen.map((rp, i) => {
        return i === index ? { ...rp, selected: !rp.selected } : rp;
      })
    );
  };

  const mergeRechnungspositionen = () => {
    setMergedRechnungspositionen([...calcRechnungspositionen.filter((rp) => rp.selected), ...vorgangRechnungspositionen.filter((rp) => rp.selected)]);
  };
  const addRow = () => {
    mergedRechnungspositionen.push({
      titel: '',
      einheit: RechnungspositionEinheit.STK,
      wert: 0,
      anzahl: 0,
      summe: 0
    });
    setMergedRechnungspositionen([...mergedRechnungspositionen]);
  };

  return (
    <Modal
      openModal={open}
      setOpenModal={onClose}
      title="Rechnungspositionen prüfen"
      body={
        <Grid container spacing={SPACING_BETWEEN_BOXES}>
          <Grid size={6}>
            <h3>Berechnete Positionen</h3>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>Rechnungsposition</TableCell>
                  <TableCell>Anzahl</TableCell>
                  <TableCell>Wert</TableCell>
                  <TableCell>Summe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {calcRechnungspositionen.map((rp, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Checkbox
                          size="small"
                          checked={rp.selected}
                          onClick={() => selectPosition(calcRechnungspositionen, index, setCalcRechnungspositionen)}
                        />
                      </TableCell>
                      <TableCell>{ermittleRechnungspositionLabel(rp.titel)}</TableCell>
                      <TableCell>
                        {rp.anzahl} {ermittleRechnungspositionEinheit(rp.einheit)}
                      </TableCell>
                      <TableCell>
                        <Euro value={rp.wert / 100} />
                      </TableCell>
                      <TableCell>
                        <Euro value={rp.summe / 100} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Grid>
          <Grid size={6}>
            <h3>Angepasste Positionen</h3>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>Rechnungsposition</TableCell>
                  <TableCell>Anzahl</TableCell>
                  <TableCell>Wert</TableCell>
                  <TableCell>Summe</TableCell>
                </TableRow>
              </TableHead>
              {vorgangRechnungspositionen?.map((rp, index) => {
                let className = '';
                if (rp.geloescht) {
                  className = 'deletedRow';
                } else if (rp.editiert) {
                  className = 'editedRow';
                }
                return (
                  <TableRow key={index} className={className}>
                    <TableCell>
                      <Checkbox
                        size="small"
                        checked={rp.selected}
                        onClick={() => selectPosition(vorgangRechnungspositionen, index, setVorgangRechnungspositionen)}
                        disabled={rp.geloescht}
                      />
                    </TableCell>
                    <TableCell width={'300px'}>{ermittleRechnungspositionLabel(rp.titel)}</TableCell>
                    <TableCell width={'100px'}>
                      {rp.anzahl} {ermittleRechnungspositionEinheit(rp.einheit)}
                    </TableCell>
                    <TableCell width={'100px'}>
                      <Euro value={rp.wert / 100} />
                    </TableCell>
                    <TableCell width={'150px'}>
                      <Euro value={rp.summe / 100} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </Table>
          </Grid>
          <Grid size={12} display="flex" justifyContent="center" margin="1rem">
            <Button variant="contained" onClick={mergeRechnungspositionen}>
              Übernehmen
            </Button>
          </Grid>
          <Grid size={12}>
            <RechnungspositionenTabelle
              reihenfolgeAenderbar={true}
              rechnungsparameter={mergedRechnungspositionen}
              setRechnungsparameter={setMergedRechnungspositionen}
              mandant={mandant}
              gutachtenart={vorgang.gutachtenart}
              positionenAenderbar={true}
              positionenLoeschbar={false}
            />
            <Grid display="flex" justifyContent="center" marginTop="1rem">
              <IconButton color="primary" onClick={addRow}>
                <AddIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      }
      footer={
        <Grid container display="flex" justifyContent="space-between" margin="1rem" width="100%">
          <Grid>
            <Button autoFocus onClick={onClose} variant="contained" color="secondary">
              Abbrechen
            </Button>
          </Grid>

          <Grid>
            <Button
              disabled={isLoading}
              variant="contained"
              color="primary"
              data-testid="speichern"
              onClick={() => aktualisiereRechnungspositionen(mergedRechnungspositionen.map((rp) => ({ ...rp, selected: undefined })))}
            >
              {saveButtonText}
            </Button>
          </Grid>
        </Grid>
      }
    />
  );
}
